/**
 * 图生文任务详情页
 * 展示批量任务的进度、子任务列表和导出 CSV 功能。
 * 任务未完成时每 5 秒自动轮询。
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import type { ReactElement } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useVirtualizer } from '@tanstack/react-virtual'
import {
  ArrowLeft,
  CheckCircle2,
  Clock,
  Download,
  Loader2,
  XCircle,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import type {
  ImageToTextTask,
  ImageToTextTaskItem,
} from '@/types/imageToTextTask'
import {
  IMAGE_TO_TEXT_TASK_STATUS_LABELS,
  IMAGE_TO_TEXT_TOOL_TYPE_LABELS,
  isImageToTextTaskTerminal,
} from '@/types/imageToTextTask'
import {
  getImageToTextTask,
  exportImageToTextTaskCSV,
} from '@/services/imageToTextTaskService'

const POLL_INTERVAL = 5000
const ROW_HEIGHT = 64

export function ImageToTextTaskDetailPage(): ReactElement {
  const { taskId } = useParams<{ taskId: string }>()
  const [task, setTask] = useState<ImageToTextTask | null>(null)
  const [items, setItems] = useState<ImageToTextTaskItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const listParentRef = useRef<HTMLDivElement>(null)

  const fetchData = useCallback(async (): Promise<void> => {
    if (taskId === undefined) return
    try {
      const data = await getImageToTextTask(taskId)
      setTask(data.task)
      setItems(data.items)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : '加载失败')
    } finally {
      setLoading(false)
    }
  }, [taskId])

  useEffect(() => {
    void fetchData()
  }, [fetchData])

  // 轮询：任务未完成时每 5 秒刷新
  useEffect(() => {
    if (task === null || isImageToTextTaskTerminal(task.status)) return

    timerRef.current = setTimeout(() => {
      void fetchData()
    }, POLL_INTERVAL)

    return (): void => {
      if (timerRef.current !== null) clearTimeout(timerRef.current)
    }
  }, [task, fetchData])

  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => listParentRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 10,
  })

  const handleExport = useCallback((): void => {
    if (taskId !== undefined) {
      exportImageToTextTaskCSV(taskId)
    }
  }, [taskId])

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (error !== null || task === null) {
    return (
      <div className="space-y-4">
        <Link to="/records/tools">
          <Button variant="ghost" size="sm">
            <ArrowLeft className="mr-1.5 h-4 w-4" />
            返回记录列表
          </Button>
        </Link>
        <p className="text-sm text-destructive">{error ?? '任务不存在'}</p>
      </div>
    )
  }

  const isTerminal = isImageToTextTaskTerminal(task.status)
  const progressPct =
    task.total_count === 0
      ? 0
      : Math.round(((task.completed_count + task.failed_count) / task.total_count) * 100)
  const statusInfo = IMAGE_TO_TEXT_TASK_STATUS_LABELS[task.status]
  const toolLabel =
    task.tool_types !== undefined && task.tool_types.length > 0
      ? task.tool_types.map((t) => IMAGE_TO_TEXT_TOOL_TYPE_LABELS[t] ?? t).join('、')
      : task.tool_type !== undefined
        ? (IMAGE_TO_TEXT_TOOL_TYPE_LABELS[task.tool_type] ?? 'AI 图像分析')
        : 'AI 图像分析'
  const canExport =
    task.status === 'completed' || task.status === 'partial_failed'

  return (
    <div className="space-y-6">
      {/* 头部 */}
      <div className="flex items-start gap-4">
        <Button asChild variant="ghost" size="icon">
          <Link to="/records/tools" aria-label="返回记录列表">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div className="space-y-1">
          <h1 className="text-2xl font-bold tracking-tight">{toolLabel}任务</h1>
          <p className="text-sm text-muted-foreground">
            提交时间：{new Date(task.created_at).toLocaleString('zh-CN')}
          </p>
        </div>
      </div>

      {/* 状态卡片 */}
      <div className="rounded-2xl border bg-card p-5 shadow-sm">
        <div className="space-y-4">
          {/* 状态 + 数量摘要 */}
          <div className="flex items-center gap-3">
            <span className={`text-sm font-semibold ${statusInfo.color}`}>
              {statusInfo.text}
            </span>
            <span className="text-sm text-muted-foreground">
              共 {task.total_count.toLocaleString()} 项 ·
              成功 {task.completed_count.toLocaleString()} ·
              失败 {task.failed_count.toLocaleString()}
            </span>
            {!isTerminal && (
              <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
            )}
          </div>

          {/* 进度条 */}
          {!isTerminal && (
            <div className="space-y-1">
              <Progress value={progressPct} className="h-2" />
              <p className="text-xs text-muted-foreground">{String(progressPct)}% 已完成</p>
            </div>
          )}

          {/* 导出按钮 */}
          {canExport && (
            <Button variant="outline" size="sm" onClick={handleExport}>
              <Download className="mr-1.5 h-3.5 w-3.5" />
              导出 CSV
            </Button>
          )}
        </div>
      </div>

      {/* 子任务列表 */}
      {items.length > 0 && (
        <div className="overflow-hidden rounded-2xl border bg-card shadow-sm">
          <div className="flex items-center justify-between border-b px-4 py-3">
            <p className="text-sm font-medium">处理结果</p>
            <p className="text-xs text-muted-foreground">共 {items.length.toLocaleString()} 项</p>
          </div>
          <div ref={listParentRef} className="h-[480px] overflow-auto">
            <div
              style={{
                height: `${String(virtualizer.getTotalSize())}px`,
                position: 'relative',
              }}
            >
              {virtualizer.getVirtualItems().map((virtualItem) => {
                const item = items[virtualItem.index]
                if (item === undefined) return null
                return (
                  <div
                    key={virtualItem.key}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: `${String(ROW_HEIGHT)}px`,
                      transform: `translateY(${String(virtualItem.start)}px)`,
                    }}
                    className="flex items-center gap-3 border-b px-4 last:border-b-0"
                  >
                    {/* 状态图标 */}
                    <div className="w-4 flex-shrink-0">
                      {item.status === 'pending' && (
                        <Clock className="h-4 w-4 text-muted-foreground/40" />
                      )}
                      {item.status === 'running' && (
                        <Loader2 className="h-4 w-4 animate-spin text-primary" />
                      )}
                      {item.status === 'success' && (
                        <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                      )}
                      {item.status === 'failed' && (
                        <XCircle className="h-4 w-4 text-destructive" />
                      )}
                    </div>

                    {/* 缩略图 */}
                    <div className="h-10 w-10 flex-shrink-0 overflow-hidden rounded bg-muted">
                      <img
                        src={item.image_url}
                        alt={item.original_filename}
                        className="h-full w-full object-cover"
                        loading="lazy"
                      />
                    </div>

                    {/* 文件名 + 工具标签 + 结果 */}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline gap-1.5">
                        <p className="truncate text-sm font-medium leading-tight">
                          {item.original_filename || item.image_url.split('/').pop()}
                        </p>
                        {item.tool_type !== undefined && (
                          <span className="flex-shrink-0 rounded-full bg-muted px-1.5 py-px text-[10px] text-muted-foreground">
                            {IMAGE_TO_TEXT_TOOL_TYPE_LABELS[item.tool_type] ?? item.tool_type}
                          </span>
                        )}
                      </div>
                      <p
                        className={`mt-0.5 line-clamp-2 text-xs leading-tight ${
                          item.status === 'failed'
                            ? 'text-destructive'
                            : 'text-muted-foreground'
                        }`}
                      >
                        {item.status === 'success' && item.result !== ''
                          ? item.result
                          : item.status === 'failed'
                            ? item.error || '处理失败'
                            : item.status === 'running'
                              ? 'AI 处理中…'
                              : '等待处理'}
                      </p>
                    </div>

                    {/* 序号 */}
                    <span className="flex-shrink-0 text-xs text-muted-foreground/40">
                      #{String(virtualItem.index + 1)}
                    </span>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
